import { STUDIO_MAX_DEVICE_SCALE } from '@/components/campaign-composer/korza-studio';

// How the Korza Studio stage (KorzaStudioStage) frames its iPhone: whole and
// centred under the pills when the stage has room, zoomed in on the part
// being edited when it does not (a phone's canvas over the editor sheet).

/**
 * The end of the device kept in view when zoomed: its top (status bar, a
 * coupon banner at the top, the lock screen clock) or its bottom (the iOS 26
 * push, a coupon banner above the tabs).
 */
export type StudioAnchor = 'top' | 'bottom';

/** Room the pills take above the device. */
export const STUDIO_PILL_SPACE_PX = 64;
const STAGE_PADDING_PX = 24;
/**
 * Below this, the whole device would be too small to read what is edited:
 * the Studio zooms in instead, as the onboarding does (ZoomedIPhoneMockup).
 */
export const STUDIO_ZOOM_BELOW_SCALE = 0.85;
const ZOOM_SIDE_PADDING_PX = 10;
const ZOOM_EDGE_PX = 12;

export interface StudioFraming {
  zoomed: boolean;
  /** Device scale, from its top centre. */
  scale: number;
  /** Device top, from the stage top, in px. */
  top: number;
}

export function frameStudioDevice({
  stageWidth,
  stageHeight,
  deviceWidth,
  deviceHeight,
  anchor,
}: {
  stageWidth: number;
  stageHeight: number;
  deviceWidth: number;
  deviceHeight: number;
  anchor: StudioAnchor;
}): StudioFraming {
  if (deviceWidth <= 0 || deviceHeight <= 0) {
    return { zoomed: false, scale: 1, top: STUDIO_PILL_SPACE_PX };
  }
  const room = stageHeight - STUDIO_PILL_SPACE_PX - STAGE_PADDING_PX;
  const fit = Math.min(
    STUDIO_MAX_DEVICE_SCALE,
    (stageWidth - STAGE_PADDING_PX * 2) / deviceWidth,
    room / deviceHeight
  );
  if (fit >= STUDIO_ZOOM_BELOW_SCALE) {
    return {
      zoomed: false,
      scale: fit,
      top: STUDIO_PILL_SPACE_PX + (room - deviceHeight * fit) / 2,
    };
  }
  const scale = Math.min(
    STUDIO_MAX_DEVICE_SCALE,
    (stageWidth - ZOOM_SIDE_PADDING_PX * 2) / deviceWidth
  );
  return {
    zoomed: true,
    scale,
    // Top: the device's top just under the pills. Bottom: its bottom edge
    // just above the stage's.
    top:
      anchor === 'top'
        ? STUDIO_PILL_SPACE_PX - 8
        : stageHeight - ZOOM_EDGE_PX - deviceHeight * scale,
  };
}

/**
 * Whether the pills above the device sit on the stage, clear of the screen.
 * Zoomed on the device's bottom, they would cover the screen itself.
 */
export function arePillsClear(
  framing: StudioFraming,
  anchor: StudioAnchor
): boolean {
  return !framing.zoomed || anchor === 'top';
}
